import React, { useState, useEffect } from 'react';
import { getTournaments } from '../api/api';
import { COUNTRY_FLAGS, EN_TO_DE_COUNTRIES } from '../constants/countries';

interface CompetitionsSectionProps {
  onSelectCompetition?: (categoryName: string, tournamentName: string) => void;
}

const STATIC_FALLBACK = [
  { name: 'Champions League', categoryName: 'International Clubs', country: null, flag: '🏆', count: 6 },
  { name: 'Championship', categoryName: 'England', country: 'England', flag: '🏴\u200D󠁢󠁥󠁮󠁧󠁿', count: 12 },
  { name: 'League One', categoryName: 'England', country: 'England', flag: '🏴\u200D󠁢󠁥󠁮󠁧󠁿', count: 4 },
  { name: 'Premier League', categoryName: 'England', country: 'England', flag: '🏴\u200D󠁢󠁥󠁮󠁧󠁿', count: 21 },
  { name: 'Bundesliga', categoryName: 'Germany', country: 'Deutschland', flag: '🇩🇪', count: 9 },
];

const SkeletonItem = () => (
  <div className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 animate-pulse">
    <div className="w-7 h-7 bg-gray-200 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2 py-1">
      <div className="h-4 bg-gray-200 rounded w-1/3" />
      <div className="h-3 bg-gray-200 rounded w-1/4" />
    </div>
    <div className="h-4 bg-gray-200 rounded w-8" />
  </div>
);

const CompetitionsSection: React.FC<CompetitionsSectionProps> = ({ onSelectCompetition }) => {
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const abortController = new AbortController();

    getTournaments(
      { sport_id: 'sr:sport:1', limit: 20, sort_by: 'event_count', sort_order: 'desc' },
      { signal: abortController.signal }
    )
      .then(data => {
        if (data?.items?.length > 0) {
          const mapped = data.items
            .filter((item: any) => item.event_count > 0)
            .map((item: any) => {
              const engCountry = item.category_name || '';
              const deCountry = EN_TO_DE_COUNTRIES[engCountry] || engCountry;
              let flag = '⚽';
              if (item.tournament_name?.includes('Champions League') || item.tournament_name?.includes('Europa League')) {
                flag = '🏆';
              } else if (deCountry) {
                flag = COUNTRY_FLAGS[deCountry] || '⚽';
              }
              return {
                name: item.tournament_name,
                id: item.tournament_id,
                categoryName: item.category_name || 'International Clubs',
                country: deCountry || null,
                flag: flag,
                count: item.event_count
              };
            }).slice(0, 5);

          setCompetitions(mapped.length > 0 ? mapped : STATIC_FALLBACK);
        } else {
          setCompetitions(STATIC_FALLBACK);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error("Failed to load dynamic top competitions for mobile:", err);
          setCompetitions(STATIC_FALLBACK);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <section className="px-3 py-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-black text-gray-900">Top-Wettbewerbe</h2>
        <button className="flex items-center gap-1 text-sm font-semibold text-gray-700">
          Alle
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <>
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
          </>
        ) : (
          competitions.map((comp, idx) => (
            <button
              key={comp.name}
              onClick={() => onSelectCompetition && onSelectCompetition(comp.categoryName, comp.name)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition ${
                idx < competitions.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <span className="text-xl w-7 text-center flex-shrink-0">{comp.flag}</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{comp.name}</p>
                {comp.country && <p className="text-xs text-gray-500">{comp.country}</p>}
              </div>
              <span className="text-sm font-semibold text-gray-700">{comp.count}</span>
            </button>
          ))
        )}
      </div>
    </section>
  );
};

export default CompetitionsSection;
